import { createSelector } from '@ngrx/store';
import { programsStateSelector, ScrollLevel } from 'src/app/store/programs.era';
import { Tick, initialise_ticks, initialise_view_control, sizeAndPlaceActivitiesAndSessions } from 'src/app/modules/onsiteschedule/onsite.helpers-unchanged';
import { eventStateSelector, JoiEvent } from '../events.era';
import { OnsiteLane, OnsiteProgram, ProgramNodeDateMap } from 'globals/store/onsite.data';
import { onsiteReselector } from './onsite.reselector';
import { Program, TimeZoneDetails, sortPrograms } from 'globals/store/programs.data';
import { N_TICKS } from 'src/app/helpers/constants';
import { Dictionary } from '@ngrx/entity';
import { fullProgramOrderReselector } from './programs.reselector';
import { justTimeToHHMM, str2Date, timeConvert } from 'globals/helpers/helpers';
import { getTimeZoneDetails } from 'src/app/helpers/helpers';
import { onsiteStateSelector } from '../onsite.era';
import { sortProgramNodesList } from 'globals/share/onsite.reselector.functions';

const dateIdReselector = createSelector(onsiteStateSelector, (state) => state.selectedDateString);
const programEntities = createSelector(programsStateSelector, (state) => state.entities);
const event = createSelector(eventStateSelector, (state) => state.event);

export const makeEmptyBlocks = () => Array.apply(null, Array(N_TICKS - 1)).map((v: null) => [])
const make_null_schedule = (): OnsiteLane => ({ laneId: '', type: 'schedule', blocks: makeEmptyBlocks(), title: '' })

const createProgramLane = (p: OnsiteProgram, ticks: Tick[], laneTemplate, program: Program): OnsiteLane => {
  // console.log("createProgramLane", p.date, { p, program })
  const program_lane: OnsiteLane = { ...laneTemplate, type: 'program', height: 0 }
  p.sessions.forEach(session => {
    if (session.type !== 'session') return
    const startTime = timeConvert(session.start)
    const endTime = timeConvert(session.end)
    program_lane.blocks[startTime.h].push({
      type: session.type,
      id: session.id,
      title: session.title,
      start: session.start,
      end: session.end,
      startTime,
      endTime,
      startMinute: startTime.h*60 + startTime.m,
      endMinute: endTime.h*60 + endTime.m,
      duration: session.duration,
      labels: session.labels ? session.labels.slice(0,4).reverse() : [],
      immediatePredecessorEndMinute: -1
    })
  })

  const sameDayAccessSetups = Object.values(program.setups || {}).filter(s => s.accessDay === p.day && s.access)
  const sameDayClearSetups = Object.values(program.setups || {}).filter(s => s.clearDay === p.day && s.clear)
  sameDayAccessSetups.forEach(setup => program_lane.blocks[setup.access.h].push({ 
    type: 'access',
    title: setup.name || 'Unnamed Setup',
    start: justTimeToHHMM(setup.access),
    startTime: setup.access,
    endTime: setup.access,
    startMinute: setup.access.h*60 + setup.access.m,
    endMinute: setup.access.h*60 + setup.access.m,
    duration: 0,
    immediatePredecessorEndMinute: -1
  }))
  sameDayClearSetups.forEach(setup => program_lane.blocks[setup.clear.h].push({ 
    type: 'clear',
    title: setup.name || 'Unnamed Setup',
    start: justTimeToHHMM(setup.clear),
    startTime: setup.clear,
    endTime: setup.clear,
    startMinute: setup.clear.h*60 + setup.clear.m,
    endMinute: setup.clear.h*60 + setup.clear.m,
    duration: 0,
    immediatePredecessorEndMinute: -1
  }))
  program_lane.blocks.forEach(b => 
    b.sort((a,b) => {
      if (a.startMinute === b.startMinute) {
        if (a.type === 'access' && b.type !== 'access') return -1
        if (a.type === 'clear' && b.type !== 'clear') return 1
        if (b.type === 'access' && a.type !== 'access') return 1
        if (b.type === 'clear' && b.type !== 'clear') return -1
        return -1 
      }
      return a.startMinute - b.startMinute
    })
  )
  // console.log({ sameDayAccessSetups, sameDayClearSetups, program_lane })


  return program_lane;
}


const makeSecondaryTicks = (secondary:TimeZoneDetails, offset:number, programDate:string): string[] => {
  const diff = offset - secondary.offset;
  const date = new Date(str2Date(programDate).getTime() + ( 1000 * 60 * 60 * 24 * (diff < 0 ? -1 : 1)));
  return Array(N_TICKS).fill(null).map((_,idx)=>{
    let tick2 = idx + diff
    let secondaryValue = ''
    if(tick2 < 0){
      tick2 = 24 + tick2
      secondaryValue = date.getDate()+' '+date.toLocaleString('default', { month: 'short' }) + ' ';
    } else if(tick2 > 24){
      tick2 = tick2 - 24
      secondaryValue = date.getDate()+' '+date.toLocaleString('default', { month: 'short' }) + ' ';
    }
    const tickValueHours = Math.floor(tick2 * 60 / 60)
    const tickValueMinutes = tick2 * 60 % 60;
    secondaryValue += (Math.abs(tickValueHours) > 9 ? tickValueHours : '0'+tickValueHours)+":"+(Math.abs(tickValueMinutes) > 9 ? tickValueMinutes : '0'+tickValueMinutes);
    return secondaryValue+' ('+secondary.abbr+')';
  })
}

// READONLY_ONSITE IS BEING DONE AWAY WITH UNTIL RESTRICTIONS IS REDONE
export const initializeSingleOssView = (programNodesByDateMap: ProgramNodeDateMap, dateId: string, currentEvent, programMap: Dictionary<Program>, programOrder: string[]): { ossView, scroll } => { // programsArray comes in sorted or


	const programNodesMap = programNodesByDateMap[dateId]
	if (!programNodesMap || !currentEvent) {
		return { ossView: null, scroll: null }
	}
	const ossView: OnsiteView[] = []
  const date = str2Date(dateId)

  const programNodesList = Array.from(programNodesMap.values())
  sortProgramNodesList(programNodesList, programOrder) 
  
	programNodesList.forEach(programNode => {
    const ticks = initialise_ticks()
		const { schedule } = programNode
    const { scheduleId, title, location, extension, day, venueId, timeZoneId } = schedule
    const venue = currentEvent.venues[venueId]
    if (timeZoneId && venue && venue.timeZoneId !== timeZoneId) {
      const tickTimes = makeSecondaryTicks(getTimeZoneDetails(venue.timeZoneId, date), getTimeZoneDetails(timeZoneId, date).offset, dateId)
      tickTimes.forEach((tickTime, i) => ticks[i].secondaryValue = tickTime)
    }
		const makeLaneTemplate = () => ({
      parentId: scheduleId,
			type: 'schedule',
			blocks: makeEmptyBlocks(),
			title,
			day: isNaN(day) ? +extension : day,
			extension,
			location
		})

    switch (schedule.type) {
      case 'program': {
        const program_lane = createProgramLane(schedule, ticks, makeLaneTemplate(), programMap[schedule.scheduleId])
        const schedule_lanes = []
        programNode.subschedules.forEach((sub: OnsiteProgram) => {
          const subLane = { ...makeLaneTemplate(), laneId: sub.id, title: sub.title }
          sub.sessions.forEach(act => subLane.blocks[timeConvert(act.time).h].push(act))
          schedule_lanes.push(subLane)
        })
        const lanes = [program_lane, ...schedule_lanes] // schedule_lanes could be empty, it's okay
        if ((!currentEvent || !currentEvent.closed) && !schedule_lanes.length) {
          lanes.push(make_null_schedule())
        }
        ossView.push({ scheduleId, ticks, lanes, venueId: schedule.venueId, timeZoneId: schedule.timeZoneId })
        break
      }
      case 'schedule': {
        const standaloneSchedule = { ...makeLaneTemplate(), standalone: true, laneId: schedule.id }
        schedule.sessions.forEach(act => standaloneSchedule.blocks[timeConvert(act.time).h].push(act))
        ossView.push({
          scheduleId,
          ticks,
          lanes: [standaloneSchedule],
          timeZoneId: schedule.timeZoneId,
          venueId: schedule.venueId,
        })
        break
      }
    }
	})

	ossView.forEach(view => {
    initialise_view_control(view)
	})

	ossView.forEach(view => {
    sizeAndPlaceActivitiesAndSessions(view)
  })
  
	let scroll_pos = 24;
	ossView.forEach(view => {
		if (view.lanes.length) {
			view.lanes[0].blocks.forEach((blk, i) => {
				if ((view.lanes[0].type === 'program' && blk.length > 0) || (view.lanes[0].type === 'schedule' && blk.length > 1)) {
					if (scroll_pos > i) {
						scroll_pos = i;
					}
				}
			});
		}
	});
	if (scroll_pos === 24) {
		scroll_pos = 9;
	}
	const scroll = { tick: scroll_pos, percentage: 0 } as ScrollLevel
	return { scroll, ossView }
}


// REMEMBER TO REFACTOR THIS TO A NORMAL SELECTOR (NOT A FUNCTION THAT RETURNS A SELECTOR) ONCE THE STORE IS UPDATED
export const onsiteBuilderReselector = createSelector(
	onsiteReselector,
  dateIdReselector,
  event,
  programEntities,
  fullProgramOrderReselector,
	({ programNodesByDate, ready },
    dateId: string,
    event: JoiEvent,
    programs,
    programOrder
	) => ({ ...initializeSingleOssView(programNodesByDate, dateId, event, programs, programOrder), ready })
)

export interface Shake { row: number, position: number }
export interface OnsiteView { venueId: string, scheduleId: string, ticks: Tick[], lanes: OnsiteLane[], shake?: Shake, controls?: number[][], timeZoneId: string }